import React from "react";
import {
  BiBriefcase,
  BiTime,
  BiGlobe,
  BiMapPin,
  BiMoney,
  BiUserCheck,
  BiCalendar,
  BiBookOpen,
  BiGroup,
  BiX,
} from "react-icons/bi";
import { EditOutlined } from "@ant-design/icons";
import ProtectedAction from "../../../Routes/ProtectedRoutes/ProtectedAction";
import { PERMISSIONS } from "../../../config/permission";
import { useTranslation } from "react-i18next";

const statusConfig = {
  published: {
    label: "OPEN",
    className: "bg-green-50 text-green-600 border border-green-200",
  },
  closed: {
    label: "CLOSED",
    className: "bg-gray-100 text-gray-500 border border-gray-200",
  },
  draft: {
    label: "DRAFT",
    className: "bg-orange-50 text-orange-500 border border-orange-200",
  },
};

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : null;

const formatSalary = (s) => {
  if (!s) return null;
  const { min, max, currency = "USD" } = s;
  if (min && max) return `${currency} ${min.toLocaleString()} – ${max.toLocaleString()}`;
  if (min) return `${currency} ${min.toLocaleString()}+`;
  return null;
};

const JobDetailPanel = ({ job, loading, onClose, onEdit, onManageApplicants }) => {
  const { t } = useTranslation("common");

  const status = job
    ? statusConfig[job.status] || {
        label: job.status,
        className: "bg-gray-100 text-gray-500 border border-gray-200",
      }
    : null;

  return (
    <>
      <style>{`
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(40px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      {/* Panel */}
      <div
        className="h-full flex flex-col bg-gray-50 overflow-hidden"
        style={{ animation: "slideInRight 0.3s ease both" }}
      >
        {/* Panel Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center">
              <BiBriefcase className="text-purple-600 text-lg" />
            </div>
            <div>
              <p className="text-xs text-gray-400 font-medium">{t("Job Details")}</p>
              <p className="text-sm font-bold text-gray-800 capitalize truncate max-w-xs">
                {loading ? "..." : job?.title || "—"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!loading && job && (
              <>
                <ProtectedAction requiredPermission={PERMISSIONS.EDIT_JOB_POSTING}>
                  <button
                    onClick={onEdit}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border border-gray-200 text-gray-600 hover:border-purple-300 hover:text-purple-600 transition-all"
                  >
                    <EditOutlined />
                    {t("Edit")}
                  </button>
                </ProtectedAction>

                <ProtectedAction requiredPermission={PERMISSIONS.VIEW_JOB_APPLICANTS}>
                  <button
                    onClick={onManageApplicants}
                    className="btn-gradient !px-3 !py-1.5 !rounded-xl !text-xs !font-semibold flex items-center gap-1.5"
                  >
                    <BiUserCheck className="text-sm" />
                    {t("Manage Applicants")}
                  </button>
                </ProtectedAction>
              </>
            )}
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading || !job ? (
            /* Skeleton */
            <div className="animate-pulse space-y-4">
              <div className="bg-white rounded-2xl p-5 border border-gray-100 space-y-3">
                <div className="flex gap-2">
                  <div className="h-5 bg-gray-200 rounded-full w-20" />
                  <div className="h-5 bg-gray-200 rounded-full w-14" />
                </div>
                <div className="h-7 bg-gray-200 rounded w-3/4" />
                <div className="flex gap-3">
                  <div className="h-4 bg-gray-200 rounded w-24" />
                  <div className="h-4 bg-gray-200 rounded w-20" />
                </div>
              </div>
              <div className="bg-white rounded-2xl p-5 border border-gray-100 h-36" />
              <div className="bg-white rounded-2xl p-5 border border-gray-100 h-24" />
              <div className="bg-white rounded-2xl p-5 border border-gray-100 h-48" />
            </div>
          ) : (
            <>
              {/* ── Hero card ───────────────────────────── */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <div className="flex flex-wrap gap-2 mb-3">
                  {job.department && (
                    <span className="text-xs font-semibold text-purple-600 bg-purple-50 px-3 py-1 rounded-full uppercase tracking-wider border border-purple-100">
                      {job.department}
                    </span>
                  )}
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider ${status.className}`}>
                    {t(status.label)}
                  </span>
                </div>

                <h2 className="text-xl font-bold text-gray-900 capitalize mb-3">
                  {job.title}
                </h2>

                <div className="flex flex-wrap gap-3 text-sm text-gray-500">
                  {job.employmentType && (
                    <span className="flex items-center gap-1.5">
                      <BiTime className="text-purple-400" />
                      <span className="capitalize">{job.employmentType}</span>
                    </span>
                  )}
                  {job.workplaceType && (
                    <span className="flex items-center gap-1.5">
                      <BiGlobe className="text-purple-400" />
                      <span className="capitalize">{job.workplaceType}</span>
                    </span>
                  )}
                  {job.location && (
                    <span className="flex items-center gap-1.5">
                      <BiMapPin className="text-purple-400" />
                      {job.location}
                    </span>
                  )}
                </div>

                {formatSalary(job.salary) && (
                  <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 text-sm">
                    <BiMoney className="text-purple-400 text-base" />
                    <span className="text-gray-500">{t("Salary")}:</span>
                    <span className="font-bold text-gray-800">{formatSalary(job.salary)}</span>
                  </div>
                )}
              </div>

              {/* ── Details grid ────────────────────────── */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <h3 className="text-sm font-bold text-gray-700 mb-4">{t("Job Details")}</h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    {
                      icon: <BiUserCheck className="text-purple-500" />,
                      label: t("Experience"),
                      value:
                        job.experience?.min != null && job.experience?.max != null
                          ? `${job.experience.min} – ${job.experience.max} ${t("yrs")}`
                          : job.experience?.min != null
                          ? `${job.experience.min}+ ${t("yrs")}`
                          : null,
                    },
                    {
                      icon: <BiBookOpen className="text-purple-500" />,
                      label: t("Education"),
                      value: job.education,
                    },
                    {
                      icon: <BiGroup className="text-purple-500" />,
                      label: t("Openings"),
                      value: job.openings,
                    },
                    {
                      icon: <BiCalendar className="text-purple-500" />,
                      label: t("From"),
                      value: formatDate(job.availableFrom),
                    },
                    {
                      icon: <BiCalendar className="text-purple-500" />,
                      label: t("Until"),
                      value: formatDate(job.availableTo),
                    },
                  ]
                    .filter((r) => r.value)
                    .map((row, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-2 p-3 rounded-xl bg-gray-50 border border-gray-100"
                      >
                        <div className="w-6 h-6 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0 mt-0.5 text-sm">
                          {row.icon}
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">{row.label}</p>
                          <p className="text-sm font-semibold text-gray-700">
                            {row.value}
                          </p>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* ── Description ─────────────────────────── */}
              {job.description && (
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                  <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                    <BiBookOpen className="text-purple-500" />
                    {t("Description")}
                  </h3>
                  <div
                    className="prose prose-sm max-w-none text-gray-600 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: job.description }}
                  />
                </div>
              )}

              {/* ── Skills ──────────────────────────────── */}
              {job.skills && job.skills.length > 0 && (
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                  <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                    <BiBriefcase className="text-purple-500" />
                    {t("Required Skills")}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {job.skills.map((skill, i) => (
                      <span
                        key={i}
                        className="px-3 py-1.5 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-100"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Interview Rounds ────────────────────── */}
              {job.rounds && job.rounds.length > 0 && (
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                  <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
                    <BiCalendar className="text-purple-500" />
                    {t("Interview Rounds")}
                  </h3>
                  <div className="space-y-3">
                    {job.rounds.map((round, i) => (
                      <div key={i} className="flex gap-3 items-start">
                        <div className="flex-shrink-0 flex flex-col items-center">
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                            {round.order || i + 1}
                          </div>
                          {i < job.rounds.length - 1 && (
                            <div className="w-0.5 h-5 bg-gray-200 mt-1" />
                          )}
                        </div>
                        <div className="flex-1 pb-1">
                          <p className="text-sm font-semibold text-gray-800 capitalize">
                            {round.name}
                          </p>
                          {round.description && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              {round.description}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default JobDetailPanel;
